import { Component } from "react";
import "../../../../styles/css/table.scss";
import "../../../../App.css";
import Helper from "../../../../Common/Helper";
import Spinner from "../../../../Components/Navigation/LoadingSpinner/Spinner";
import { RouteComponentProps, RouterProps } from "react-router-dom";
import { appContext } from "../../../../AppContext";
import { DateTime } from "luxon";
import { DateHelper } from "../../../../Common/DateHelper";
import { Box, Grid, SelectChangeEvent } from "@mui/material";
import IbssDataGrid from "../../../../Components/Data/DataGrid/IbssDataGrid";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import IbssSvgIcon from "../../../../Components/Icons/SvgIcon/IbssSvgIcon";
import IbssFilter from "../../../../Components/Forms/Filter/IbssFilter";
import EditIcon from "@mui/icons-material/Edit";
import IbssFilterHeader from "../../../../Components/Forms/DateRange/IbssFilterHeader";
import { IUserNotifications } from "../../../../Providers.Api/UserNotifications/GeManyEndpoint";
import { UserNotificationsFilter } from "../../../../Providers.Api/UserNotifications/UserNotificationsRepository";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { QueryParams } from "./QueryParams";
import { DateRangeHelper } from "../../../../Common/DateRangeHelper";
import { StringHelper } from "../../../../Common/StringHelper";
import { Constants } from "../../../../Common/Constants";

class ListUserNotifications extends IbssPage<IProps, IState, QueryParams>
{
    private get alert() { return appContext().alert; }
    private get appState() { return appContext().state; }
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private pageCacheKey = '418a435a-3lda-41n7-bb1a-1y9876ba4c4b';

    private string = new StringHelper();

    constructor(props: IProps) 
    {
        super(props, new QueryParams());
        this.state =
        {
            isLoading: false,
            NotificationList: [],
            buildingId: this.appState.buildingId,
            searchTerm: "",
            selectedNotificationId: [],
            disableCancel: true,
            daysFilter: this.labels.HubLabelToday,
            startDate: DateHelper.now(),
            endDate: DateHelper.now(),
            dateDisabled: true,
        };
    }

    public async queryParamsDidUpdate(firstLoad: boolean, prevParams: QueryParams): Promise<void>
    {
        if (firstLoad)
        {
            this.clearCache();
        }

        const queryParams = this.queryParams;
        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: this.appState.buildingId }, true);
        }

        const buildingId = queryParams.building;
        const buildingName = this.local.getNodeData().Regions.flatMap(i => i.Buildings).find(i => i.Node_Id == buildingId)?.Name;
        this.pageTitle = (buildingName ? this.labels.HubLabelFacilityManagementText + buildingName : this.labels.HubLabelFacilityManagement);

        const dateRange = new DateRangeHelper(buildingId)
            .addToday()
            .addTomorrow()
            .addThisWeek()
            .addNextWeek()
            .addThisMonth()
            .addNextMonth()
            .getDateRange(queryParams.period, this.labels.HubLabelToday);

        if (queryParams.period != dateRange.period)
        {
            return this.pushQueryParams({ period: dateRange.period }, true);
        }

        await this.setStateAsync({
            buildingId: buildingId,
            daysFilter: dateRange.period,
            dateDisabled: (dateRange.period != this.labels.HubLabelCustom),
            startDate: dateRange.start,
            endDate: dateRange.end,
            searchTerm: queryParams.filter ?? '',
        });

        await this.loadNotifications();
    }

    private clearCache(): void
    {
        appContext().inMemoryCache.clear(i => i.startsWith(this.pageCacheKey + '_'));
    }

    private async loadNotifications(): Promise<void> 
    {
        try
        {
            this.setState({ isLoading: true });

            const endpoint = {
                name: 'apiClient.userNotifications.getMany',
                buildingId: this.state.buildingId,
                filter: new UserNotificationsFilter({
                    startDate: this.state.startDate.setZoneByNode(this.state.buildingId).startOf('day'),
                    endDate: this.state.endDate.setZoneByNode(this.state.buildingId).plus({ days: 1 }).startOf('day'),
                    classification: 'Building'
                }),
            };

            const userNotificationsData = await appContext().inMemoryCache.lazyGetWithQuickExpiry(
                `${this.pageCacheKey}_${JSON.stringify(endpoint)}`,
                () => appContext().apiClient.userNotifications.getMany(endpoint.buildingId, endpoint.filter),
            );

            const notificationsList = userNotificationsData.map(i => NotificationView.fromResponse(i));
            await this.setStateAsync({ NotificationList: notificationsList });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async dateRangeChanged(e: SelectChangeEvent<string>): Promise<void>
    {
        this.pushQueryParams({ period: e.target.value });
    }

    private filterNotifications(searchTerm: string): NotificationView[]
    {
        const filteredNotifications = this.state.NotificationList.filter(notification =>
        {
            let key: keyof NotificationView;
            for (key in notification)
            {
                if (notification[key]?.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase()))
                {
                    return true;
                }
            }
            return false;
        });
        return filteredNotifications;
    }

    private notificationSelected(selection: GridRowSelectionModel): void
    {
        const selectedOrders: NotificationView[] = [];
        selection.forEach((id) =>
        {
            const orderData = this.filterNotifications(this.state.searchTerm).find(i => i.id === id);
            if (orderData)
            {
                selectedOrders.push(orderData)
            }
        });

        if (selectedOrders.length > 0)
        {
            this.setState({
                selectedNotificationId: selectedOrders,
                disableCancel: false,
            })
        }
        else 
        {
            this.setState({
                disableCancel: true,
            })
        }
    }


    private renderNotificationAction(value: number, nodeId: number): React.ReactNode
    {
        return (
            <>
                <IbssSvgIcon
                    className="pointer"
                    onClick={() => this.props.history.push(`/operational-services-notifications/${value}/notifications/${nodeId}`)}
                >
                    <EditIcon />
                </IbssSvgIcon>
            </>
        );
    }

    private debounceFilterTextTimer: NodeJS.Timeout | null = null;
    private searchTermChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void
    {
        this.setState({ searchTerm: event.target.value });
        clearTimeout(this.debounceFilterTextTimer ?? undefined);

        this.debounceFilterTextTimer = setTimeout(
            () => this.pushQueryParams({ filter: event.target.value === '' ? undefined : event.target.value }),
            Constants.longDebounceTimeInMilliseconds,
        );
    }


    public render(): JSX.Element 
    {
        const dataGridHeader: GridColDef<NotificationView>[] = [
            {
                headerName: this.labels.HubLabelClass,
                field: Helper.nameOf<NotificationView>("classification"),
                minWidth: 150,
            },
            {
                headerName: this.labels.HubLabelSubClass,
                field: Helper.nameOf<NotificationView>("subClassification"),
                minWidth: 250,
            },
            {
                headerName: this.labels.funcNotificationsEventCreatedAt_L,
                field: Helper.nameOf<NotificationView>("eventCreatedAt"),
                minWidth: 150,
                flex: 2,
                valueGetter: params => params.row.eventCreatedAt && params.row.eventCreatedAt.toLocaleDateTimeString()
            },
            {
                headerName: this.labels.HubLabelAction,
                field: "",
                minWidth: 150,
                flex: 1,
                renderCell: (params) => this.renderNotificationAction(params.row.nodeId, params.row.id)
            },
        ];

        const filteredNotifications = this.filterNotifications(this.state.searchTerm);

        return (
            <>
                <Box className="page-height-exct-header">
                    <div className="rightPanel-main-content">
                        <div className="table-panel">
                            <IbssFilterHeader
                                title={this.labels.HubLabelNotifications}
                                description={this.labels.funcSystemNotificationTableDescriptionPt1_Message}
                                selectedPreset={this.state.daysFilter}
                                presetChanged={(e) => this.dateRangeChanged(e)}
                                startDate={this.state.startDate.toJSDate()}
                                endDate={this.state.endDate.toJSDate()}
                                datesDisabled={this.state.dateDisabled}
                                excludeCustom={true}
                                presets={[this.labels.HubLabelTomorrow, this.labels.HubLabelThisWeek, this.labels.HubLabelNextWeek, this.labels.HubLabelThisMonth, this.labels.HubLabelNextMonth]}
                            />
                            <Grid
                                container
                                rowSpacing={1}
                                sx={{ display: "flex", alignItems: "center", mt: 0, ml: 0 }}
                            >
                                <Grid item md={12}>
                                    <Box>
                                        <IbssFilter
                                            showExport={false}
                                            searchTerm={this.state.searchTerm}
                                            onSearchTermChange={(event) => this.searchTermChange(event)}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            <div className="position-relative">
                                {this.state.isLoading && <Spinner />}
                                <Box sx={{ mt: 1 }}>
                                    <IbssDataGrid
                                        height="calc(100vh - 318px)"
                                        checkboxSelection
                                        columns={dataGridHeader}
                                        disableRowSelectionOnClick
                                        rows={filteredNotifications}
                                        onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) => this.notificationSelected(rowSelectionModel)}
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 25 } },
                                        }}
                                        pageSizeOptions={[25, 50, 100]}
                                    />
                                </Box>
                            </div>
                        </div>
                    </div>
                </Box>
            </>
        );
    }
}

class NotificationView
{
    public id = 0;
    public nodeId = 0;
    public classification = "";
    public subClassification = "";
    public eventCreatedAt: DateTime | null = null;

    constructor(data: Partial<NotificationView>)
    {
        Object.assign(this, data);
    }

    public static fromResponse(menu: IUserNotifications)
    {
        const menuView = new NotificationView(
            {
                id: menu.Notification_Id,
                nodeId: menu.Node_Id,
                classification: menu.Classification,
                subClassification: menu.Subclassification,
                eventCreatedAt: menu._EventTime.offsetTimeByNode(menu.Node_Id),
            });
        return menuView;
    }
}

interface IProps extends RouterProps, RouteComponentProps<IMatchParams>
{
}

interface IMatchParams
{
    buildingid: string;
}

interface IState 
{
    isLoading: boolean;
    buildingId: number;
    disableCancel: boolean;
    NotificationList: NotificationView[];
    searchTerm: string;
    selectedNotificationId: NotificationView[];
    daysFilter: string;
    startDate: DateTime;
    endDate: DateTime;
    dateDisabled: boolean;
}

export default ListUserNotifications;
